import { useEffect, useState } from 'react';

export default function ContactSection() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    message: '',
  });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
          }
        });
      },
      { threshold: 0.1 }
    );

    const elements = document.querySelectorAll('.contact-animate');
    elements.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    if (name === 'message' && value.length > 500) return;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.phone) return;

    setSubmitting(true);
    const params = new URLSearchParams();
    params.append('name', formData.name);
    params.append('email', formData.email);
    params.append('phone', formData.phone);
    params.append('company', formData.company);
    params.append('message', formData.message);

    try {
      const response = await fetch('https://readdy.ai/api/form/d814jr8m1au3l14kc5b0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: params.toString(),
      });

      if (response.ok) {
        setSubmitted(true);
        setFormData({ name: '', email: '', phone: '', company: '', message: '' });
        setTimeout(() => setSubmitted(false), 5000);
      }
    } catch (err) {
      console.error('Error al enviar mensaje:', err);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="contacto" className="py-20 md:py-32 bg-yakis-white relative overflow-hidden">
      {/* Decorative */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-yakis-gold/5 rounded-full blur-3xl" />

      <div className="section-container relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20">
          {/* Left - Info */}
          <div>
            <div className="contact-animate animate-on-scroll mb-4">
              <span className="text-yakis-gold text-sm font-semibold tracking-wider uppercase">
                Contacto
              </span>
            </div>

            <h2 className="contact-animate animate-on-scroll font-display font-bold text-3xl sm:text-4xl md:text-5xl text-yakis-navy leading-tight mb-6">
              Comienza a
              <br />
              <span className="text-yakis-gold">transformar tu movilidad</span>
            </h2>

            <p className="contact-animate animate-on-scroll text-yakis-silver text-base md:text-lg leading-relaxed mb-10">
              Estamos listos para diseñar la solución de transporte perfecta para tu empresa.
              Contáctanos y recibe una cotización personalizada en menos de 24 horas.
            </p>

            {/* Contact Cards */}
            <div className="space-y-4">
              <div className="contact-animate animate-on-scroll flex items-center gap-4 p-4 bg-white rounded-xl shadow-sm">
                <div className="w-12 h-12 flex items-center justify-center rounded-full bg-green-50">
                  <i className="ri-whatsapp-line text-xl text-green-600" />
                </div>
                <div>
                  <p className="font-display font-medium text-sm text-yakis-navy">WhatsApp</p>
                  <a href="https://wa.me/524441951948" target="_blank" rel="noopener noreferrer nofollow" className="text-sm text-yakis-silver hover:text-yakis-gold transition-colors">
                    +52 444 195 1948
                  </a>
                </div>
              </div>

              <div className="contact-animate animate-on-scroll flex items-center gap-4 p-4 bg-white rounded-xl shadow-sm">
                <div className="w-12 h-12 flex items-center justify-center rounded-full bg-yakis-gold/10">
                  <i className="ri-mail-line text-xl text-yakis-gold" />
                </div>
                <div>
                  <p className="font-display font-medium text-sm text-yakis-navy">Correo Electrónico</p>
                  <a href="mailto://ventas@yakis.com.mx" className="text-sm text-yakis-silver hover:text-yakis-gold transition-colors">
                    ventas@yakis.com.mx
                  </a>
                </div>
              </div>

              <div className="contact-animate animate-on-scroll flex items-center gap-4 p-4 bg-white rounded-xl shadow-sm">
                <div className="w-12 h-12 flex items-center justify-center rounded-full bg-yakis-gold/10">
                  <i className="ri-phone-line text-xl text-yakis-gold" />
                </div>
                <div>
                  <p className="font-display font-medium text-sm text-yakis-navy">Teléfono</p>
                  <a href="tel://+524441951948" className="text-sm text-yakis-silver hover:text-yakis-gold transition-colors">
                    +52 444 195 1948
                  </a>
                </div>
              </div>

              <div className="contact-animate animate-on-scroll flex items-center gap-4 p-4 bg-white rounded-xl shadow-sm">
                <div className="w-12 h-12 flex items-center justify-center rounded-full bg-yakis-gold/10">
                  <i className="ri-map-pin-line text-xl text-yakis-gold" />
                </div>
                <div>
                  <p className="font-display font-medium text-sm text-yakis-navy">Oficina Principal</p>
                  <a href="https://" target="_blank" rel="noopener noreferrer nofollow" className="text-sm text-yakis-silver hover:text-yakis-gold transition-colors">
                    San Luis Potosí, México
                  </a>
                </div>
              </div>
            </div>
          </div>

          {/* Right - Form */}
          <div className="contact-animate animate-on-scroll">
            <div className="bg-white rounded-2xl p-6 md:p-8 shadow-lg">
              <h3 className="font-display font-semibold text-xl text-yakis-navy mb-6">
                Envíanos un mensaje
              </h3>

              {submitted ? (
                <div className="text-center py-10">
                  <div className="w-16 h-16 flex items-center justify-center rounded-full bg-green-50 mx-auto mb-4">
                    <i className="ri-check-line text-3xl text-green-600" />
                  </div>
                  <p className="font-display font-semibold text-lg text-yakis-navy mb-2">
                    ¡Mensaje enviado!
                  </p>
                  <p className="text-sm text-yakis-silver">
                    Nos pondremos en contacto contigo pronto.
                  </p>
                </div>
              ) : (
                <form
                  id="contacto-yakis-form"
                  data-readdy-form
                  action="https://readdy.ai/api/form/d814jr8m1au3l14kc5b0"
                  method="POST"
                  onSubmit={handleSubmit}
                  className="space-y-4"
                >
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="contact-name" className="block text-sm font-medium text-yakis-navy mb-1.5">
                        Nombre completo
                      </label>
                      <input
                        id="contact-name"
                        type="text"
                        name="name"
                        required
                        value={formData.name}
                        onChange={handleChange}
                        className="w-full px-4 py-3 rounded-xl border border-yakis-cream bg-yakis-white/50 text-sm text-yakis-navy placeholder:text-yakis-silverLight focus:outline-none focus:ring-2 focus:ring-yakis-gold/50 focus:border-yakis-gold transition-all"
                        placeholder="Tu nombre"
                      />
                    </div>
                    <div>
                      <label htmlFor="contact-company" className="block text-sm font-medium text-yakis-navy mb-1.5">
                        Empresa
                      </label>
                      <input
                        id="contact-company"
                        type="text"
                        name="company"
                        value={formData.company}
                        onChange={handleChange}
                        className="w-full px-4 py-3 rounded-xl border border-yakis-cream bg-yakis-white/50 text-sm text-yakis-navy placeholder:text-yakis-silverLight focus:outline-none focus:ring-2 focus:ring-yakis-gold/50 focus:border-yakis-gold transition-all"
                        placeholder="Nombre de la empresa"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="contact-email" className="block text-sm font-medium text-yakis-navy mb-1.5">
                        Correo electrónico
                      </label>
                      <input
                        id="contact-email"
                        type="email"
                        name="email"
                        required
                        value={formData.email}
                        onChange={handleChange}
                        className="w-full px-4 py-3 rounded-xl border border-yakis-cream bg-yakis-white/50 text-sm text-yakis-navy placeholder:text-yakis-silverLight focus:outline-none focus:ring-2 focus:ring-yakis-gold/50 focus:border-yakis-gold transition-all"
                        placeholder="correo@empresa.com"
                      />
                    </div>
                    <div>
                      <label htmlFor="contact-phone" className="block text-sm font-medium text-yakis-navy mb-1.5">
                        Teléfono
                      </label>
                      <input
                        id="contact-phone"
                        type="tel"
                        name="phone"
                        required
                        value={formData.phone}
                        onChange={handleChange}
                        className="w-full px-4 py-3 rounded-xl border border-yakis-cream bg-yakis-white/50 text-sm text-yakis-navy placeholder:text-yakis-silverLight focus:outline-none focus:ring-2 focus:ring-yakis-gold/50 focus:border-yakis-gold transition-all"
                        placeholder="444 123 4567"
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="contact-message" className="block text-sm font-medium text-yakis-navy mb-1.5">
                      Mensaje
                    </label>
                    <textarea
                      id="contact-message"
                      name="message"
                      rows={4}
                      maxLength={500}
                      value={formData.message}
                      onChange={handleChange}
                      className="w-full px-4 py-3 rounded-xl border border-yakis-cream bg-yakis-white/50 text-sm text-yakis-navy placeholder:text-yakis-silverLight focus:outline-none focus:ring-2 focus:ring-yakis-gold/50 focus:border-yakis-gold transition-all resize-none"
                      placeholder="Cuéntanos sobre tus necesidades de transporte..."
                    />
                    <p className="text-xs text-yakis-silverLight mt-1 text-right">
                      {formData.message.length}/500
                    </p>
                  </div>

                  <button
                    type="submit"
                    disabled={submitting}
                    className="w-full btn-primary py-4 text-base inline-flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <i className={`ri-${submitting ? 'loader-4-line animate-spin' : 'send-plane-line'}`} />
                    {submitting ? 'Enviando...' : 'Enviar mensaje'}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}